import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useV2ListCollaboratorsForAccountQuery,
  useV2ListLineOfBusinessesForAccountQuery,
} from "@generated";
import { AdminFormPage, saveErrorToast } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { ApprovalLevelForm } from "../ApprovalLevelForm/ApprovalLevelForm";
import { useApprovalLevelMutations } from "../api/useApprovalLevelMutations";
import type { ApprovalLevelFormValues } from "../types/ApprovalLevel";

const FORM_ID = "approval-level-form";

const initial: ApprovalLevelFormValues = {
  name: "",
  kind: "business",
  signaturesRequired: 1,
  submitterMayApprove: false,
  limitApprovalsToTeam: false,
  triggers: [],
  approvers: [],
};

export const New = () => {
  const navigate = useNavigate();
  const { create } = useApprovalLevelMutations();
  const { data: usersData } = useV2ListCollaboratorsForAccountQuery({});
  const { data: lobsData } = useV2ListLineOfBusinessesForAccountQuery({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const availableUsers = useMemo(
    () =>
      (usersData?.data ?? []).map((u: any) => ({
        id: String(u.id),
        name: u.attributes?.name ?? u.attributes?.identifier ?? "",
      })),
    [usersData]
  );

  const availableLobs = useMemo(
    () =>
      (lobsData?.data ?? []).map((l: any) => ({
        id: String(l.id),
        name: l.attributes?.["nested-name"] ?? l.attributes?.name ?? "",
      })),
    [lobsData]
  );

  const handleSubmit = async (values: ApprovalLevelFormValues) => {
    setSubmitting(true);
    try {
      const result: any = await create(values);
      const newId = result?.data?.id;
      if (newId) {
        navigate(`/admin/approval_levels/${newId}/edit`, {
          state: {
            toast: { variant: "success", text: "Approval level created." },
          },
        });
      } else {
        setToast({ variant: "success", text: "Approval level created." });
      }
    } catch (err) {
      console.error("Failed to create approval level", err);
      setToast(saveErrorToast(err, "approval level"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Add an Approval Level"
        formId={FORM_ID}
        onCancel={() => navigate("/admin/approval_levels")}
        isSubmitting={submitting}
      >
        <ApprovalLevelForm
          id={FORM_ID}
          initialValues={initial}
          availableUsers={availableUsers}
          availableLobs={availableLobs}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
